import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCaja } from '../contexts/CajaContext';
import { getApiUrl } from '../config/apiConfig';
import clientPrinterService from '../services/clientPrinterService';
import './Pago.css';

const PagoComponent = () => {
  const { pedidoId, detalleId } = useParams();
  const navigate = useNavigate();
  const { auth } = useAuth();
  const { cajaActiva, cajasAbiertas } = useCaja();

  const [pedido, setPedido] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metodoPago, setMetodoPago] = useState('');
  const [mensajeExito, setMensajeExito] = useState('');
  const [mostrarConfirmacion, setMostrarConfirmacion] = useState(false);
  const [marcarComoEntregado, setMarcarComoEntregado] = useState(false);
  const [imprimiendoComprobante, setImprimiendoComprobante] = useState(false);

  // Estados para Pago Mixto por Montos Libres (Opción B)
  const [tipoVariosMode, setTipoVariosMode] = useState('montos'); // 'montos' | 'productos'
  const [montoEfectivo, setMontoEfectivo] = useState('');
  const [montoTarjeta, setMontoTarjeta] = useState('');
  const [montoYape, setMontoYape] = useState('');
  const [montoPlin, setMontoPlin] = useState('');

  // Barra/cocina — solo aplica a pedidos directos cuando NO se marca como entregado
  const [barras, setBarras] = useState([]);
  const [barraSeleccionada, setBarraSeleccionada] = useState(null);
  // Barra asignada al MOZO en tiempo real (evita JWT desactualizado)
  const [barraAsignadaDB, setBarraAsignadaDB] = useState(auth?.user?.id_barra_asignada || null);

  // Estado para verificar si se puede procesar el pago
  const [puedeProcedarPago, setPuedeProcedarPago] = useState(true);
  const [mensajeError, setMensajeError] = useState('');

  // Nuevo estado para notificación flotante
  const [notificacion, setNotificacion] = useState({
    visible: false,
    mensaje: '',
    tipo: 'info' // 'info', 'warning', 'error'
  });

  // Métodos de pago disponibles
  const metodosPago = ['efectivo', 'tarjeta', 'yape', 'plin', 'varios'];

  // Variable para determinar si es pago individual, pago completo o pago seleccionado
  const esPagoIndividual = !!detalleId;
  // Manejar productos seleccionados
  const [productosSeleccionados, setProductosSeleccionados] = useState([]);
  const esPagoSeleccionado = pedidoId && !detalleId && location.pathname.includes('/seleccionados');

  // Estado para manejar los productos agrupados en el comprobante
  const [productosAgrupados, setProductosAgrupados] = useState([]);

  // Caja a la que se asigna el cobro
  // CAJERO → automático (cajaActiva), MOZO/ENCARGADO → debe seleccionar
  const [idCajaCobro, setIdCajaCobro] = useState(null);

  // Caja asignada al MOZO obtenida en tiempo real desde la DB (evita JWT desactualizado)
  const [cajaAsignadaDB, setCajaAsignadaDB] = useState(auth?.user?.id_caja_asignada || null);

  // Constante para la mesa de pedidos directos
  const MESA_DIRECTA_ID = 910100;
  const MESA_DIRECTA_NUMERO = 0;

  useEffect(() => {
    cargarDatos();

    // Si es pago de productos seleccionados, cargar los IDs desde sessionStorage
    if (esPagoSeleccionado) {
      const seleccionados = JSON.parse(sessionStorage.getItem('productosSeleccionados') || '[]');
      setProductosSeleccionados(seleccionados);
    }
  }, [pedidoId, detalleId, auth?.token]);

  useEffect(() => {
    // Agrupar productos cuando se cargue el pedido
    if (pedido) {
      agruparProductosParaComprobante();
      verificarEstadoProductos();
    }
  }, [pedido, detalle, productosSeleccionados, marcarComoEntregado, barraSeleccionada, barraAsignadaDB, barras]);

  // CAJERO: asignar automáticamente su caja activa al cobro
  // MOZO con caja asignada: pre-seleccionar esa caja si está abierta (usa dato de DB, no JWT)
  // GENERAL: Si solo hay una caja abierta, auto-seleccionarla
  useEffect(() => {
    if (auth?.user?.rol === 'CAJERO' && cajaActiva?.id) {
      setIdCajaCobro(cajaActiva.id);
    } else if (auth?.user?.rol === 'MOZO' && cajaAsignadaDB) {
      const cajaAsig = cajasAbiertas.find(c => c.id === cajaAsignadaDB);
      if (cajaAsig) setIdCajaCobro(cajaAsig.id);
    } else if (cajasAbiertas.length === 1) {
      setIdCajaCobro(cajasAbiertas[0].id);
    }
  }, [cajaActiva?.id, cajasAbiertas, auth?.user?.rol, cajaAsignadaDB]);

  // Fetch real-time de asignaciones del MOZO para evitar JWT desactualizado
  useEffect(() => {
    if (!auth?.token || !['MOZO', 'CAJERO'].includes(auth?.user?.rol)) return;
    fetch(getApiUrl('/usuarios-admin/mis-asignaciones'), {
      headers: { 'Authorization': `Bearer ${auth.token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setCajaAsignadaDB(data.id_caja_asignada || null);
          setBarraAsignadaDB(data.id_barra_asignada || null);
        }
      })
      .catch(() => { });
  }, [auth?.token]);

  // Cargar barras disponibles (solo para pedidos directos)
  useEffect(() => {
    if (!pedido || pedido.id_mesa !== MESA_DIRECTA_ID) return;
    fetch(getApiUrl('/barras'), {
      headers: { 'Authorization': `Bearer ${auth?.token}` }
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        setBarras(data);
        // Si MOZO o CAJERO tiene barra asignada y está en la lista, auto-seleccionarla
        if (['MOZO', 'CAJERO'].includes(auth?.user?.rol) && barraAsignadaDB) {
          const barraExiste = data.find(b => b.id === barraAsignadaDB);
          if (barraExiste) setBarraSeleccionada(barraAsignadaDB);
        }
      })
      .catch(() => { });
  }, [pedido?.id_mesa, auth?.token, barraAsignadaDB]);

  // Nueva función para verificar el estado de los productos
  const verificarEstadoProductos = () => {
    // Si no hay pedido o detalles, no hacer nada
    if (!pedido || !pedido.detalles) return;

    // Para pedidos directos, permitir cualquier estado pero requerir barra/cocina si no se entrega al pagar
    if (esPedidoMesaDirecta()) {
      const tieneAsignacionManual = !!barraSeleccionada;
      const tieneAsignacionDB = !!barraAsignadaDB || !!pedido?.id_barra || !!detalle?.id_barra;
      const tieneDestino = tieneAsignacionManual || tieneAsignacionDB;

      const hayBarrasDisponibles = barras.length > 0;
      const debeSeleccionar = !marcarComoEntregado && hayBarrasDisponibles;

      if (debeSeleccionar && !tieneDestino) {
        setPuedeProcedarPago(false);
        setMensajeError('Por favor seleccione una barra o cocina para los productos.');
      } else {
        setPuedeProcedarPago(true);
        setMensajeError('');
      }
      return;
    }

    // Para pago individual
    if (esPagoIndividual && detalle) {
      if (detalle.estado !== 'entregado') {
        setPuedeProcedarPago(false);
        setMensajeError('Este producto no puede ser pagado porque no ha sido entregado');
      } else {
        setPuedeProcedarPago(true);
        setMensajeError('');
      }
      return;
    }

    // Para pago de productos seleccionados
    if (esPagoSeleccionado) {
      const productosNoEntregados = pedido.detalles.filter(
        d => productosSeleccionados.includes(d.id) &&
          d.estado !== 'entregado' &&
          d.estado !== 'pagado' &&
          d.estado !== 'pendiente_pagado' &&
          d.estado !== 'cancelado'
      );

      if (productosNoEntregados.length > 0) {
        setPuedeProcedarPago(false);
        setMensajeError('No se puede procesar el pago porque hay productos seleccionados que no han sido entregados');
        mostrarNotificacion('No se puede procesar el pago porque hay productos seleccionados que no han sido entregados', 'warning');
      } else {
        setPuedeProcedarPago(true);
        setMensajeError('');
      }
      return;
    }

    // Para pago completo
    const productosNoEntregados = pedido.detalles.filter(
      d => d.estado !== 'entregado' &&
        d.estado !== 'pagado' &&
        d.estado !== 'pendiente_pagado' &&
        d.estado !== 'cancelado'
    );

    if (productosNoEntregados.length > 0) {
      setPuedeProcedarPago(false);
      setMensajeError('No se puede procesar el pago porque hay productos que no han sido entregados');
      mostrarNotificacion('No se puede procesar el pago porque hay productos que no han sido entregados', 'warning');
    } else {
      setPuedeProcedarPago(true);
      setMensajeError('');
    }
  };

  // Función para mostrar notificaciones
  const mostrarNotificacion = (mensaje, tipo = 'info') => {
    setNotificacion({
      visible: true,
      mensaje,
      tipo
    });

    // Auto-ocultar después de 5 segundos
    setTimeout(() => {
      setNotificacion(prev => ({ ...prev, visible: false }));
    }, 5000);
  };

  const cargarDatos = async () => {
    try {
      setLoading(true);

      // Cargar datos del pedido
      const responsePedido = await fetch(getApiUrl(`/pedidos/${pedidoId}`), {
        headers: {
          'Authorization': `Bearer ${auth?.token}`
        }
      });

      if (!responsePedido.ok) {
        throw new Error('Error al cargar los datos del pedido');
      }

      const dataPedido = await responsePedido.json();


      setPedido(dataPedido);

      // Si es un pedido de mesa directa, permitir pago en cualquier estado
      if (dataPedido.id_mesa === MESA_DIRECTA_ID || dataPedido.numero_mesa === MESA_DIRECTA_NUMERO) {
        setPuedeProcedarPago(true);
        setLoading(false);

        // Si es pago individual y hay detalle, cargarlo igualmente
        if (esPagoIndividual && detalleId) {
          const detalleEncontrado = dataPedido.detalles.find(d => d.id === parseInt(detalleId));
          if (detalleEncontrado) {
            setDetalle(detalleEncontrado);
          }
        }
        return;
      }

      // Si es pago individual, cargar el detalle específico
      if (esPagoIndividual && detalleId) {
        const detalleEncontrado = dataPedido.detalles.find(d => d.id === parseInt(detalleId));

        if (!detalleEncontrado) {
          mostrarNotificacion('Producto no encontrado en el pedido', 'error');
          setLoading(false);
          return;
        }

        // Verificar si el producto está entregado
        if (detalleEncontrado.estado !== 'entregado') {
          mostrarNotificacion('Este producto no puede ser pagado porque no ha sido entregado', 'warning');
          // Aún así, establecemos el detalle para mostrar la información
          setDetalle(detalleEncontrado);
          setPuedeProcedarPago(false);
        } else {
          setDetalle(detalleEncontrado);
          setPuedeProcedarPago(true);
        }
      }
      // Si es pago de productos seleccionados
      else if (esPagoSeleccionado) {
        const seleccionados = JSON.parse(sessionStorage.getItem('productosSeleccionados') || '[]');

        if (seleccionados.length === 0) {
          mostrarNotificacion('No hay productos seleccionados para pagar', 'warning');
          setPuedeProcedarPago(false);
        }

        // Guardar los detalles de los productos seleccionados
        setProductosSeleccionados(seleccionados);

        // Verificar si hay productos no entregados entre los seleccionados
        const productosNoEntregados = dataPedido.detalles.filter(
          d => seleccionados.includes(d.id) &&
            d.estado !== 'entregado' &&
            d.estado !== 'pagado' &&
            d.estado !== 'pendiente_pagado' &&
            d.estado !== 'cancelado'
        );

        if (productosNoEntregados.length > 0) {
          mostrarNotificacion('No se puede procesar el pago porque hay productos seleccionados que no han sido entregados', 'warning');
          setPuedeProcedarPago(false);
        } else {
          setPuedeProcedarPago(true);
        }
      } else {
        // Verificar si todos los productos están entregados, pagados o cancelados
        const productosNoEntregados = dataPedido.detalles.filter(
          d => d.estado !== 'entregado' &&
            d.estado !== 'pagado' &&
            d.estado !== 'pendiente_pagado' &&
            d.estado !== 'cancelado'
        );

        if (productosNoEntregados.length > 0) {
          mostrarNotificacion('No se puede procesar el pago porque hay productos que no han sido entregados', 'warning');
          setPuedeProcedarPago(false);
        } else {
          setPuedeProcedarPago(true);
        }
      }

      setLoading(false);
    } catch (err) {
      console.error('Error al cargar datos:', err);
      setError(err.message || 'Error al cargar datos para el pago');
      setLoading(false);
    }

  };

  // Nueva función para agrupar productos similares
  const agruparProductosParaComprobante = () => {
    let productosParaAgrupar = [];

    if (esPagoIndividual && detalle) {
      // Para pago individual no necesitamos agrupar
      setProductosAgrupados([detalle]);
      return;
    }

    if (!pedido || !pedido.detalles) return;

    // Seleccionar los productos adecuados según el tipo de pago
    if (esPedidoMesaDirecta()) {
      // Para pedidos directos, incluir todos los productos no pagados sin importar su estado
      productosParaAgrupar = esPagoSeleccionado
        ? pedido.detalles.filter(d =>
          productosSeleccionados.includes(d.id) &&
          d.estado !== 'pagado' &&
          d.estado !== 'pendiente_pagado' &&
          d.estado !== 'cancelado')
        : pedido.detalles.filter(d =>
          d.estado !== 'pagado' &&
          d.estado !== 'pendiente_pagado' &&
          d.estado !== 'cancelado');
    } else {
      // Para pedidos normales, solo incluir productos entregados
      productosParaAgrupar = esPagoSeleccionado
        ? pedido.detalles.filter(d =>
          productosSeleccionados.includes(d.id) &&
          d.estado === 'entregado' &&
          d.estado !== 'pagado' &&
          d.estado !== 'pendiente_pagado' &&
          d.estado !== 'cancelado')
        : pedido.detalles.filter(d =>
          d.estado === 'entregado' &&
          d.estado !== 'pagado' &&
          d.estado !== 'pendiente_pagado' &&
          d.estado !== 'cancelado');
    }

    // Asegurarse de que productosParaAgrupar tenga elementos
    console.log('Productos para agrupar:', productosParaAgrupar);

    // Objeto para almacenar los grupos por id_producto y nombre
    const grupos = {};

    productosParaAgrupar.forEach(d => {
      // Usamos id_producto y nombre como clave para asegurar que productos iguales se agrupen
      const clave = `${d.id_producto}-${d.nombre_producto}`;

      if (!grupos[clave]) {
        grupos[clave] = {
          ...d,
          cantidad_total: d.cantidad,
          subtotal: d.cantidad * d.precio_unitario
        };
      } else {
        grupos[clave].cantidad_total += d.cantidad;
        grupos[clave].subtotal += d.cantidad * d.precio_unitario;
      }
    });

    // Debug para ver los productos agrupados
    console.log('Productos agrupados:', Object.values(grupos));

    setProductosAgrupados(Object.values(grupos));
  };

  const verificarLimitacionesMetodoPago = () => {
    // Siempre devolvemos todos los métodos de pago disponibles
    return metodosPago;
  };

  const metodosPagoDisponibles = verificarLimitacionesMetodoPago();

  const calcularTotal = () => {
    // Para pedidos directos, incluir todos los productos no pagados sin importar su estado
    if (esPedidoMesaDirecta()) {
      if (esPagoIndividual && detalle) {
        return detalle.cantidad * detalle.precio_unitario;
      } else if (esPagoSeleccionado && pedido) {
        return pedido.detalles
          .filter(d =>
            productosSeleccionados.includes(d.id) &&
            d.estado !== 'pagado' &&
            d.estado !== 'pendiente_pagado' &&
            d.estado !== 'cancelado'
          )
          .reduce((sum, d) => sum + (d.cantidad * d.precio_unitario), 0);
      } else if (pedido) {
        return pedido.detalles
          .filter(d =>
            d.estado !== 'pagado' &&
            d.estado !== 'pendiente_pagado' &&
            d.estado !== 'cancelado'
          )
          .reduce((sum, d) => sum + (d.cantidad * d.precio_unitario), 0);
      }
    } else {
      // Para pedidos normales, solo incluir productos entregados
      if (esPagoIndividual && detalle) {
        return detalle.estado === 'entregado' ? detalle.cantidad * detalle.precio_unitario : 0;
      } else if (esPagoSeleccionado && pedido) {
        return pedido.detalles
          .filter(d =>
            productosSeleccionados.includes(d.id) &&
            d.estado === 'entregado' &&
            d.estado !== 'pagado' &&
            d.estado !== 'pendiente_pagado' &&
            d.estado !== 'cancelado'
          )
          .reduce((sum, d) => sum + (d.cantidad * d.precio_unitario), 0);
      } else if (pedido) {
        return pedido.detalles
          .filter(d =>
            d.estado === 'entregado' &&
            d.estado !== 'pagado' &&
            d.estado !== 'pendiente_pagado' &&
            d.estado !== 'cancelado'
          )
          .reduce((sum, d) => sum + (d.cantidad * d.precio_unitario), 0);
      }
    }
    return 0;
  };

  const calcularTotalPagado = () => {
    if (!pedido) return 0;
    return pedido.total_pago || 0;
  };

  const calcularTotalPendiente = () => {
    if (!pedido) return 0;
    return pedido.total - calcularTotalPagado();
  };

  const formatearFecha = (timestamp) => {
    if (!timestamp) return 'N/A';
    const fecha = new Date(timestamp);
    return fecha.toLocaleString();
  };

  // Función para determinar si el pedido es de la mesa directa
  const esPedidoMesaDirecta = () => {
    return pedido && (
      pedido.id_mesa === MESA_DIRECTA_ID ||
      pedido.numero_mesa === MESA_DIRECTA_NUMERO
    );
  };

  const handlePagar = () => {
    if (!metodoPago) {
      mostrarNotificacion('Debes seleccionar un método de pago', 'warning');
      return;
    }

    // Validación para Pago Mixto por Montos Libres
    if (metodoPago === 'varios' && tipoVariosMode === 'montos') {
      const totalAPagar = calcularTotal();
      const sum = (parseFloat(montoEfectivo) || 0) + 
                  (parseFloat(montoTarjeta) || 0) + 
                  (parseFloat(montoYape) || 0) + 
                  (parseFloat(montoPlin) || 0);
      if (Math.abs(sum - totalAPagar) > 0.01) {
        mostrarNotificacion(`La suma de los montos (S/. ${sum.toFixed(2)}) debe ser exactamente igual al total a pagar (S/. ${totalAPagar.toFixed(2)})`, 'warning');
        return;
      }
    }

    // Permitir pago para pedidos directos sin verificación de estado
    if (esPedidoMesaDirecta()) {
      setMostrarConfirmacion(true);
      return;
    }

    // Verificar si se puede proceder con el pago para pedidos normales
    if (!puedeProcedarPago) {
      mostrarNotificacion(mensajeError, 'warning');
      return;
    }

    // Para pago individual
    if (esPagoIndividual && detalle) {
      if (detalle.estado !== 'entregado') {
        mostrarNotificacion('Este producto no puede ser pagado porque no ha sido entregado', 'warning');
        return;
      }
    }

    // Para pago completo o productos seleccionados
    const hayProductosParaPagar = esPagoIndividual
      ? detalle?.estado === 'entregado'
      : esPagoSeleccionado
        ? pedido.detalles.some(d =>
          productosSeleccionados.includes(d.id) &&
          d.estado === 'entregado' &&
          d.estado !== 'pagado' &&
          d.estado !== 'pendiente_pagado' &&
          d.estado !== 'cancelado')
        : pedido.detalles.some(d =>
          d.estado === 'entregado' &&
          d.estado !== 'pagado' &&
          d.estado !== 'pendiente_pagado' &&
          d.estado !== 'cancelado');

    if (!hayProductosParaPagar) {
      mostrarNotificacion('No hay productos entregados disponibles para pagar', 'warning');
      return;
    }

    setMostrarConfirmacion(true);
  };

  // Función para verificar si hay métodos de pago diferentes en los productos
  const verificarMetodosPagoDiferentes = () => {
    // Si no hay pedido o detalles, retornamos false
    if (!pedido || !pedido.detalles) return false;

    // Obtenemos los métodos de pago únicos de los productos pagados
    const metodosUnicos = new Set();

    pedido.detalles.forEach(d => {
      if ((d.estado === 'pagado' || d.estado === 'pendiente_pagado') && d.metodo_pago_producto) {
        metodosUnicos.add(d.metodo_pago_producto);
      }
    });

    // Si hay más de un método de pago o si el nuevo pago usa un método diferente
    if (metodosUnicos.size > 1 || (metodosUnicos.size === 1 && !metodosUnicos.has(metodoPago))) {
      return true;
    }

    return false;
  };

  const confirmarPago = async () => {
    try {
      if (metodoPago === 'varios' && tipoVariosMode === 'montos') {
        await pagarPedidoMixtoMonto();
      } else if (esPagoIndividual) {
        await pagarProducto();
      } else if (esPagoSeleccionado) {
        await pagarProductosSeleccionados();
      } else {
        await pagarPedidoCompleto();
      }
    } catch (err) {
      mostrarNotificacion(err.message || 'Error al procesar el pago', 'error');
    }
  };

  const pagarPedidoMixtoMonto = async () => {
    try {
      const totalAPagar = calcularTotal();
      
      // 1. Obtener los productos que se están pagando en esta acción
      let productosAPagar = [];
      if (esPagoIndividual && detalle) {
        productosAPagar = [detalle];
      } else if (esPagoSeleccionado) {
        productosAPagar = pedido.detalles.filter(d => 
          productosSeleccionados.includes(d.id) &&
          d.estado !== 'pagado' &&
          d.estado !== 'pendiente_pagado' &&
          d.estado !== 'cancelado'
        );
      } else {
        productosAPagar = esPedidoMesaDirecta()
          ? pedido.detalles.filter(d => d.estado !== 'pagado' && d.estado !== 'pendiente_pagado' && d.estado !== 'cancelado')
          : pedido.detalles.filter(d => d.estado === 'entregado' && d.estado !== 'pagado' && d.estado !== 'pendiente_pagado' && d.estado !== 'cancelado');
      }

      const estadoProducto = esPedidoMesaDirecta()
        ? marcarComoEntregado ? 'entregado_pagado' : 'pendiente_pagado'
        : 'pagado';

      // 2. Actualizar el estado de estos productos en detalles_pedido
      const promesasDetalles = productosAPagar.map(d =>
        fetch(getApiUrl(`/pedidos/${pedidoId}/detalles/${d.id}`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth?.token}`
          },
          body: JSON.stringify({
            estado: estadoProducto,
            metodo_pago_producto: 'varios'
          })
        })
      );
      await Promise.all(promesasDetalles);

      // 3. Calcular los nuevos totales acumulados para el pedido
      const nuevoEfectivo = Number(pedido.pago_efectivo || 0) + Number(montoEfectivo || 0);
      const nuevoTarjeta = Number(pedido.pago_tarjeta || 0) + Number(montoTarjeta || 0);
      const nuevoYape = Number(pedido.pago_yape || 0) + Number(montoYape || 0);
      const nuevoPlin = Number(pedido.pago_plin || 0) + Number(montoPlin || 0);
      
      const totalPagadoActual = calcularTotalPagado();
      const nuevoTotalPagado = totalPagadoActual + totalAPagar;

      // 4. Actualizar el pedido principal
      const bodyPedido = {
        total_pago: nuevoTotalPagado,
        id_caja: pedido?.id_caja || idCajaCobro || cajaActiva?.id || null,
        pago_efectivo: nuevoEfectivo,
        pago_tarjeta: nuevoTarjeta,
        pago_yape: nuevoYape,
        pago_plin: nuevoPlin
      };

      if (esPedidoMesaDirecta() && !marcarComoEntregado && barraSeleccionada) {
        bodyPedido.id_barra = barraSeleccionada;
      }

      // Si con este cobro se cubren todos los productos del pedido, marcar el pedido como pagado completo
      const todosProductosPagados = pedido.detalles.every(d =>
        d.estado === 'pagado' || d.estado === 'pendiente_pagado' || d.estado === 'entregado_pagado' || d.estado === 'cancelado' || productosAPagar.some(ap => ap.id === d.id)
      );

      if (todosProductosPagados) {
        bodyPedido.estado = esPedidoMesaDirecta()
          ? marcarComoEntregado ? 'entregado_pagado' : 'pendiente_pagado'
          : 'pagado';
        bodyPedido.metodo_pago = 'varios';
      }

      const responsePedido = await fetch(getApiUrl(`/pedidos/${pedidoId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth?.token}`
        },
        body: JSON.stringify(bodyPedido)
      });

      if (!responsePedido.ok) {
        throw new Error('Error al actualizar el pedido principal');
      }

      setMensajeExito('Pago mixto por montos procesado correctamente');
      setMostrarConfirmacion(false);
      navigate(`/pedidos/${pedidoId}`);
    } catch (err) {
      mostrarNotificacion(err.message || 'Error al procesar el pago mixto', 'error');
    }
  };

  const pagarProducto = async () => {
    try {
      // Solo verificar estado si no es pedido directo
      if (!esPedidoMesaDirecta() && detalle.estado !== 'entregado') {
        mostrarNotificacion('Este producto no puede ser pagado porque no ha sido entregado', 'warning');
        return;
      }

      const totalAPagar = detalle.cantidad * detalle.precio_unitario;

      // Determinar el estado a utilizar basado en la mesa
      const estadoProducto = esPedidoMesaDirecta()
        ? marcarComoEntregado ? 'entregado_pagado' : 'pendiente_pagado'
        : 'pagado';

      // Actualizar el estado del detalle a pagado y registrar método de pago
      const responseDetalle = await fetch(getApiUrl(`/pedidos/${pedidoId}/detalles/${detalleId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth?.token}`
        },
        body: JSON.stringify({
          estado: estadoProducto,
          metodo_pago_producto: metodoPago
        })
      });

      if (!responseDetalle.ok) {
        throw new Error('Error al actualizar el estado del producto');
      }

      // Actualizar el total pagado en el pedido
      const totalPagadoActual = calcularTotalPagado();
      const nuevoTotalPagado = totalPagadoActual + totalAPagar;

      const bodyActualizacion = {
        total_pago: nuevoTotalPagado,
        id_caja: pedido?.id_caja || idCajaCobro || cajaActiva?.id || null,
      };
      if (esPedidoMesaDirecta() && !marcarComoEntregado && barraSeleccionada) {
        bodyActualizacion.id_barra = barraSeleccionada;
      }

      const responsePedido = await fetch(getApiUrl(`/pedidos/${pedidoId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth?.token}`
        },
        body: JSON.stringify(bodyActualizacion)
      });

      if (!responsePedido.ok) {
        throw new Error('Error al actualizar el total pagado del pedido');
      }

      // Si todos los productos están pagados, actualizar estado del pedido
      const todosProductosPagados = pedido.detalles.every(d =>
        d.estado === 'pagado' || d.estado === 'pendiente_pagado' || d.estado === 'entregado_pagado' || d.estado === 'cancelado' || d.id === parseInt(detalleId)
      );

      if (todosProductosPagados) {
        // Verificar si hay métodos de pago diferentes
        const metodoFinalPedido = verificarMetodosPagoDiferentes() ? 'varios' : metodoPago;

        // Determinar el estado del pedido basado en la mesa
        // Modifica la función que determina el estado del pedido
        const determinarEstadoPedido = () => {
          if (esPedidoMesaDirecta()) {
            return marcarComoEntregado ? 'entregado_pagado' : 'pendiente_pagado';
          }
          return 'pagado';
        };

        const estadoPedido = determinarEstadoPedido();

        await fetch(getApiUrl(`/pedidos/${pedidoId}`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth?.token}`
          },
          body: JSON.stringify({
            estado: estadoPedido,
            metodo_pago: metodoFinalPedido,
            id_caja: pedido?.id_caja || idCajaCobro || cajaActiva?.id || null,
          })
        });
      }

      setMensajeExito('Pago procesado correctamente');
      setMostrarConfirmacion(false);
      navigate(`/pedidos/${pedidoId}`);
    } catch (err) {
      mostrarNotificacion(err.message, 'error');
    }
  };

  const pagarPedidoCompleto = async () => {
    try {
      // Comprobar si todos los productos están entregados (excepto en pedidos directos)
      if (!esPedidoMesaDirecta()) {
        const productosNoEntregados = pedido.detalles.filter(d =>
          d.estado !== 'entregado' &&
          d.estado !== 'pagado' &&
          d.estado !== 'pendiente_pagado' &&
          d.estado !== 'cancelado'
        );

        if (productosNoEntregados.length > 0) {
          mostrarNotificacion('No se puede procesar el pago porque hay productos que no han sido entregados', 'warning');
          return;
        }
      }

      // Para pedidos directos, obtener todos los productos no pagados
      const productosAPagar = esPedidoMesaDirecta()
        ? pedido.detalles.filter(d =>
          d.estado !== 'pagado' &&
          d.estado !== 'pendiente_pagado' &&
          d.estado !== 'cancelado'
        )
        : pedido.detalles.filter(d =>
          d.estado === 'entregado' &&
          d.estado !== 'pagado' &&
          d.estado !== 'pendiente_pagado' &&
          d.estado !== 'cancelado'
        );

      if (productosAPagar.length === 0) {
        mostrarNotificacion('No hay productos entregados disponibles para pagar', 'warning');
        return;
      }

      // Determinar el estado a utilizar basado en la mesa
      const estadoProducto = esPedidoMesaDirecta()
        ? marcarComoEntregado ? 'entregado_pagado' : 'pendiente_pagado'
        : 'pagado';

      // Actualizar cada detalle no pagado
      const promesasDetalles = productosAPagar.map(detalle =>
        fetch(getApiUrl(`/pedidos/${pedidoId}/detalles/${detalle.id}`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth?.token}`
          },
          body: JSON.stringify({
            estado: estadoProducto,
            metodo_pago_producto: metodoPago
          })
        })
      );

      await Promise.all(promesasDetalles);

      // Verificar si hay productos ya pagados con métodos diferentes
      const metodoFinalPedido = verificarMetodosPagoDiferentes() ? 'varios' : metodoPago;

      // Determinar el estado del pedido basado en la mesa
      const estadoPedido = esPedidoMesaDirecta()
        ? marcarComoEntregado ? 'entregado_pagado' : 'pendiente_pagado'
        : 'pagado';

      // Actualizar el pedido completo
      const bodyPedidoCompleto = {
        estado: estadoPedido,
        metodo_pago: metodoFinalPedido,
        total_pago: Number(pedido.total),
        id_caja: pedido?.id_caja || idCajaCobro || cajaActiva?.id || null,
      };
      if (esPedidoMesaDirecta() && !marcarComoEntregado && barraSeleccionada) {
        bodyPedidoCompleto.id_barra = barraSeleccionada;
      }

      const responsePedido = await fetch(getApiUrl(`/pedidos/${pedidoId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth?.token}`
        },
        body: JSON.stringify(bodyPedidoCompleto)
      });

      if (!responsePedido.ok) {
        throw new Error('Error al actualizar el estado del pedido');
      }

      setMensajeExito('Pago del pedido completo procesado correctamente');
      setMostrarConfirmacion(false);
      navigate(`/pedidos/${pedidoId}`);
    } catch (err) {
      mostrarNotificacion(err.message, 'error');
    }
  };

  const pagarProductosSeleccionados = async () => {
    try {
      // Verificar si hay productos no entregados entre los seleccionados (excepto en pedidos directos)
      if (!esPedidoMesaDirecta()) {
        const productosNoEntregados = pedido.detalles.filter(
          d => productosSeleccionados.includes(d.id) &&
            d.estado !== 'entregado' &&
            d.estado !== 'pagado' &&
            d.estado !== 'pendiente_pagado' &&
            d.estado !== 'cancelado'
        );

        if (productosNoEntregados.length > 0) {
          mostrarNotificacion('No se puede procesar el pago porque hay productos seleccionados que no han sido entregados', 'warning');
          return;
        }
      }

      // Obtener los productos a pagar
      const productosAPagar = esPedidoMesaDirecta()
        ? pedido.detalles.filter(
          d => productosSeleccionados.includes(d.id) &&
            d.estado !== 'pagado' &&
            d.estado !== 'pendiente_pagado' &&
            d.estado !== 'cancelado'
        )
        : pedido.detalles.filter(
          d => productosSeleccionados.includes(d.id) &&
            d.estado === 'entregado' &&
            d.estado !== 'pagado' &&
            d.estado !== 'pendiente_pagado' &&
            d.estado !== 'cancelado'
        );

      if (productosAPagar.length === 0) {
        mostrarNotificacion('No hay productos válidos para pagar', 'warning');
        return;
      }

      // Determinar el estado a utilizar basado en la mesa
      const estadoProducto = esPedidoMesaDirecta()
        ? marcarComoEntregado ? 'entregado_pagado' : 'pendiente_pagado'
        : 'pagado';

      // Actualizar cada detalle seleccionado
      const promesasDetalles = productosAPagar.map(detalle =>
        fetch(getApiUrl(`/pedidos/${pedidoId}/detalles/${detalle.id}`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth?.token}`
          },
          body: JSON.stringify({
            estado: estadoProducto,
            metodo_pago_producto: metodoPago
          })
        })
      );

      await Promise.all(promesasDetalles);

      // Calcular el total pagado
      const totalAPagar = productosAPagar.reduce(
        (sum, d) => sum + (d.cantidad * d.precio_unitario), 0
      );

      // Actualizar el total pagado en el pedido
      const totalPagadoActual = calcularTotalPagado();
      const nuevoTotalPagado = totalPagadoActual + totalAPagar;

      const bodyParcialSel = {
        total_pago: nuevoTotalPagado,
        id_caja: pedido?.id_caja || idCajaCobro || cajaActiva?.id || null,
      };
      if (esPedidoMesaDirecta() && !marcarComoEntregado && barraSeleccionada) {
        bodyParcialSel.id_barra = barraSeleccionada;
      }

      const responsePedido = await fetch(getApiUrl(`/pedidos/${pedidoId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth?.token}`
        },
        body: JSON.stringify(bodyParcialSel)
      });

      if (!responsePedido.ok) {
        throw new Error('Error al actualizar el total pagado del pedido');
      }

      // Verificar si todos los productos están pagados ahora
      const todosProductosPagados = pedido.detalles.every(d =>
        d.estado === 'pagado' ||
        d.estado === 'pendiente_pagado' ||
        d.estado === 'cancelado' ||
        productosSeleccionados.includes(d.id)
      );

      if (todosProductosPagados) {
        // Verificar si hay métodos de pago diferentes
        const metodoFinalPedido = verificarMetodosPagoDiferentes() ? 'varios' : metodoPago;

        // Determinar el estado del pedido basado en la mesa
        const estadoPedido = esPedidoMesaDirecta()
          ? marcarComoEntregado ? 'entregado_pagado' : 'pendiente_pagado'
          : 'pagado';

        const bodyFinalSel = {
          estado: estadoPedido,
          metodo_pago: metodoFinalPedido,
          id_caja: pedido?.id_caja || idCajaCobro || cajaActiva?.id || null,
        };
        if (esPedidoMesaDirecta() && !marcarComoEntregado && barraSeleccionada) {
          bodyFinalSel.id_barra = barraSeleccionada;
        }

        await fetch(getApiUrl(`/pedidos/${pedidoId}`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth?.token}`
          },
          body: JSON.stringify(bodyFinalSel)
        });
      }

      // Limpiar el sessionStorage
      sessionStorage.removeItem('productosSeleccionados');

      setMensajeExito('Pago de productos seleccionados procesado correctamente');
      setMostrarConfirmacion(false);
      navigate(`/pedidos/${pedidoId}`);
    } catch (err) {
      mostrarNotificacion(err.message, 'error');
    }
  };

  const generarComprobante = async () => {
    agruparProductosParaComprobante();

    // Determinar filas del comprobante según tipo de pago
    const itemsComprobante = esPagoIndividual && detalle
      ? [{
        nombre: detalle.nombre_producto || `Producto ${detalle.id_producto}`,
        cantidad: detalle.cantidad,
        precio: detalle.precio_unitario,
        subtotal: (detalle.cantidad * detalle.precio_unitario)
      }]
      : productosAgrupados.map(p => ({
        nombre: p.nombre_producto || `Producto ${p.id_producto}`,
        cantidad: p.cantidad_total || p.cantidad,
        precio: p.precio_unitario,
        subtotal: ((p.cantidad_total || p.cantidad) * p.precio_unitario)
      }));

    const mesaLabel = esPedidoMesaDirecta()
      ? 'PEDIDO DIRECTO'
      : (pedido?.numero_mesa || pedido?.id_mesa ? `Mesa ${pedido.numero_mesa || pedido.id_mesa}` : 'S/N');

    const cajaActualObj = cajasAbiertas?.find(c => c.id === (pedido?.id_caja || idCajaCobro));
    const cajaLabel = cajaActualObj?.nombre || (pedido?.id_caja || idCajaCobro ? `Caja #${pedido?.id_caja || idCajaCobro}` : 'Caja Principal');
    const cajeroNombre = auth?.user?.nombre || auth?.user?.name || pedido?.nombre_usuario || 'Cajero';

    const datosComprobante = {
      pedidoId: pedidoId || pedido?.id,
      mesa: mesaLabel,
      piso: pedido?.piso || pedido?.piso_nombre || '',
      cajero: cajeroNombre,
      caja: cajaLabel,
      fecha: new Date().toLocaleDateString('es-PE'),
      hora: new Date().toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' }),
      metodoPago: metodoPago || 'Efectivo',
      pago_efectivo: parseFloat(montoEfectivo || 0),
      pago_tarjeta: parseFloat(montoTarjeta || 0),
      pago_yape: parseFloat(montoYape || 0),
      pago_plin: parseFloat(montoPlin || 0),
      total: calcularTotal(),
      items: itemsComprobante,
      userId: auth?.user?.id,
      estado: pedido?.estado
    };

    setImprimiendoComprobante(true);
    try {
      const res = await clientPrinterService.imprimirComprobanteCliente(datosComprobante);
      if (res.success) {
        mostrarNotificacion('Comprobante emitido correctamente en la impresora asignada', 'info');
      } else if (res.queued) {
        mostrarNotificacion(res.message || 'Sin conexión a la impresora. Guardado en la cola offline.', 'warning');
      } else {
        mostrarNotificacion(res.message || 'Fallo al imprimir comprobante', 'error');
      }
    } catch (err) {
      console.error('Error al emitir comprobante:', err);
      mostrarNotificacion(`Error al emitir comprobante: ${err.message}`, 'error');
    } finally {
      setImprimiendoComprobante(false);
    }
  };

  if (loading) {
    return <div className="loading">Cargando datos de pago...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error">Error: {error}</div>
        <button onClick={() => navigate(`/pedidos/${pedidoId}`)} className="btn-volver">
          Volver al Pedido
        </button>
      </div>
    );
  }

  return (
    <div className="pago-container">
      {mensajeExito && (
        <div className="mensaje-exito">
          {mensajeExito}
        </div>
      )}

      {/* Notificación flotante */}
      {notificacion.visible && (
        <div className={`notificacion-flotante ${notificacion.tipo}`}>
          <div className="notificacion-contenido">
            <span className="notificacion-mensaje">{notificacion.mensaje}</span>
            <button
              className="cerrar-notificacion"
              onClick={() => setNotificacion(prev => ({ ...prev, visible: false }))}
            >
              ×
            </button>
          </div>
        </div>
      )}

      <div className="pago-header">
        <h2>
          {esPagoIndividual
            ? 'Pago de Producto'
            : esPagoSeleccionado
              ? 'Pago de Productos Seleccionados'
              : 'Pago de Pedido Completo'}
        </h2>
        <button onClick={() => navigate(`/pedidos/${pedidoId}`)} className="btn-volver">
          ← Volver al Pedido
        </button>
      </div>

      {/* Mensaje de advertencia para productos no entregados */}
      {!puedeProcedarPago && (
        <div className="mensaje-advertencia">
          {mensajeError}
        </div>
      )}

      <div className="pago-info">
        <div className="info-pedido">
          <div className="info-header-summary">
            <h3>Información del Pedido</h3>
            <span className="pedido-badge">#{pedido?.id}</span>
          </div>

          <div className="info-grid-pago">
            <div className="info-item">
              <span className="info-label">Fecha</span>
              <span className="info-value">{formatearFecha(pedido?.tiempo_inicio)}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Mesa / Origen</span>
              <span className="info-value destacada">
                {esPedidoMesaDirecta() ? '⚡ PEDIDO DIRECTO' : `🍽️ Mesa ${pedido?.numero_mesa || pedido?.id_mesa}`}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Atendido por</span>
              <span className="info-value">{pedido?.nombre_usuario || 'Personal'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Caja Asignada</span>
              <span className="info-value">
                {cajasAbiertas?.find(c => c.id === (pedido?.id_caja || idCajaCobro))?.nombre || `Caja #${pedido?.id_caja || idCajaCobro || '---'}`}
              </span>
            </div>
          </div>

          <div className="resumen-pagos-nuevo">
            <div className="resumen-pago-col">
              <span className="resumen-label">Total Pedido</span>
              <span className="resumen-monto">S/. {Number(pedido?.total || 0).toFixed(2)}</span>
            </div>
            <div className="resumen-pago-col">
              <span className="resumen-label">Pagado</span>
              <span className="resumen-monto pagado">S/. {calcularTotalPagado().toFixed(2)}</span>
            </div>
            <div className="resumen-pago-col resaltado">
              <span className="resumen-label">Pendiente</span>
              <span className="resumen-monto pendiente">S/. {calcularTotalPendiente().toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="detalles-pago">
          <h3>{esPagoIndividual ? 'Detalle del Producto' : 'Productos a Pagar'}</h3>

          <table className="tabla-pago">
            <thead>
              <tr>
                <th>Producto</th>
                <th>Cant.</th>
                <th>Precio</th>
                <th>Subtotal</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {esPagoIndividual ? (
                <tr className={detalle?.estado !== 'entregado' ? 'producto-no-entregado' : ''}>
                  <td data-label="Producto">{detalle?.nombre_producto || `Producto ${detalle?.id_producto}`}</td>
                  <td data-label="Cantidad">{detalle?.cantidad}</td>
                  <td data-label="Precio">S/. {detalle?.precio_unitario}</td>
                  <td data-label="Subtotal">S/. {(detalle?.cantidad * detalle?.precio_unitario).toFixed(2)}</td>
                  <td data-label="Estado">{detalle?.estado.replace('_', ' ')}</td>
                </tr>
              ) : (
                productosAgrupados.map((producto, index) => (
                  <tr key={index}>
                    <td data-label="Producto">{producto.nombre_producto || `Producto ${producto.id_producto}`}</td>
                    <td data-label="Cantidad">{producto.cantidad_total || producto.cantidad}</td>
                    <td data-label="Precio">S/. {producto.precio_unitario}</td>
                    <td data-label="Subtotal">S/. {((producto.cantidad_total || producto.cantidad) * producto.precio_unitario).toFixed(2)}</td>
                    <td data-label="Estado">{producto.estado.replace('_', ' ')}</td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="3" className="total-label">Total a Pagar:</td>
                <td colSpan="2" className="total-value">S/. {calcularTotal().toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="metodo-pago">
          <h3>Método de Pago</h3>
          <div className="opciones-pago">
            {metodosPagoDisponibles.map(metodo => (
              <button
                key={metodo}
                className={`btn-metodo-pago ${metodoPago === metodo ? 'seleccionado' : ''}`}
                onClick={() => setMetodoPago(metodo)}
              >
                {metodo.charAt(0).toUpperCase() + metodo.slice(1)}
              </button>
            ))}

            {metodoPago === 'varios' && (
              <div className="varios-split-calculator" style={{
                background: '#f8fafc',
                border: '1px solid #cbd5e1',
                borderRadius: '12px',
                padding: '1.25rem',
                marginTop: '1.5rem',
                width: '100%',
                textAlign: 'left'
              }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.95rem' }}>
                  📊 Calculadora de Pago Mixto (Montos Libres)
                </h4>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
                  {/* Efectivo */}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 'bold', color: '#475569', marginBottom: '0.35rem' }}>
                      💵 Monto Efectivo (S/.)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={montoEfectivo}
                      onChange={(e) => setMontoEfectivo(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.55rem 0.75rem',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e1',
                        fontSize: '0.95rem'
                      }}
                    />
                  </div>

                  {/* Tarjeta */}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 'bold', color: '#475569', marginBottom: '0.35rem' }}>
                      💳 Monto Tarjeta (S/.)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={montoTarjeta}
                      onChange={(e) => setMontoTarjeta(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.55rem 0.75rem',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e1',
                        fontSize: '0.95rem'
                      }}
                    />
                  </div>

                  {/* Yape */}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 'bold', color: '#475569', marginBottom: '0.35rem' }}>
                      📱 Monto Yape (S/.)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={montoYape}
                      onChange={(e) => setMontoYape(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.55rem 0.75rem',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e1',
                        fontSize: '0.95rem'
                      }}
                    />
                  </div>

                  {/* Plin */}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 'bold', color: '#475569', marginBottom: '0.35rem' }}>
                      🌀 Monto Plin (S/.)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={montoPlin}
                      onChange={(e) => setMontoPlin(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.55rem 0.75rem',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e1',
                        fontSize: '0.95rem'
                      }}
                    />
                  </div>
                </div>

                {/* Validador Dinámico */}
                {(() => {
                  const totalAPagar = calcularTotal();
                  const sum = (parseFloat(montoEfectivo) || 0) + 
                              (parseFloat(montoTarjeta) || 0) + 
                              (parseFloat(montoYape) || 0) + 
                              (parseFloat(montoPlin) || 0);
                  const diff = totalAPagar - sum;

                  if (Math.abs(diff) < 0.01) {
                    return (
                      <div style={{
                        marginTop: '1.25rem',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        background: '#ecfdf5',
                        borderLeft: '4px solid #10b981',
                        color: '#065f46',
                        fontWeight: '600',
                        fontSize: '0.85rem'
                      }}>
                        ✓ Monto exacto cubierto correctamente (Total: S/. {totalAPagar.toFixed(2)}).
                      </div>
                    );
                  } else if (diff > 0) {
                    return (
                      <div style={{
                        marginTop: '1.25rem',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        background: '#fffbeb',
                        borderLeft: '4px solid #f59e0b',
                        color: '#92400e',
                        fontWeight: '600',
                        fontSize: '0.85rem'
                      }}>
                        ⚠️ Suma actual: S/. {sum.toFixed(2)} | Faltan ingresar S/. {diff.toFixed(2)}.
                      </div>
                    );
                  } else {
                    return (
                      <div style={{
                        marginTop: '1.25rem',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        background: '#fef2f2',
                        borderLeft: '4px solid #ef4444',
                        color: '#991b1b',
                        fontWeight: '600',
                        fontSize: '0.85rem'
                      }}>
                        ❌ Suma actual: S/. {sum.toFixed(2)} | Sobran S/. {Math.abs(diff).toFixed(2)}.
                      </div>
                    );
                  }
                })()}
              </div>
            )}

            {esPedidoMesaDirecta() && (
              <div className="opcion-entrega">
                <label>
                  <input
                    type="checkbox"
                    checked={marcarComoEntregado}
                    onChange={(e) => {
                      setMarcarComoEntregado(e.target.checked);
                      if (e.target.checked) setBarraSeleccionada(null);
                    }}
                  />
                  Marcar como entregado al pagar
                </label>

                <p className="info-pedido-directo">
                  Si marca esta opción, el pedido pasará de estado "pendiente" a "entregado" y no se mostrara en Barra/Cocina.
                </p>

                {/* Barra — solo mostrar si no está marcada como entregada Y no tiene barra ya asignada el pedido */}
                {!marcarComoEntregado && (
                  <div style={{ marginTop: '10px' }}>
                    {/* Caso 1: MOZO/CAJERO con barra fija asignada en su perfil */}
                    {['MOZO', 'CAJERO'].includes(auth?.user?.rol) && barraAsignadaDB ? (
                      <p style={{ fontSize: '0.85rem', color: '#4a5568', margin: 0 }}>
                        Barra: <strong>{barras.find(b => b.id === (barraAsignadaDB || pedido?.id_barra || detalle?.id_barra))?.nombre || `Barra #${barraAsignadaDB || pedido?.id_barra || detalle?.id_barra}`}</strong>
                      </p>
                    ) : (pedido?.id_barra || detalle?.id_barra) ? (
                      // Caso 2: El pedido ya tiene una barra asignada desde su creación
                      <p style={{ fontSize: '0.85rem', color: '#4a5568', margin: 0 }}>
                        Barra asignada: <strong>{barras.find(b => b.id === (pedido?.id_barra || detalle?.id_barra))?.nombre || `Barra #${pedido?.id_barra || detalle?.id_barra}`}</strong>
                      </p>
                    ) : barras.length > 0 ? (
                      // Caso 3: Sin asignación → selector manual (solo si el sistema tiene barras configuradas)
                      <>
                        <p style={{ fontSize: '0.85rem', fontWeight: 600, color: '#4a5568', marginBottom: '6px', marginTop: 0 }}>
                          ¿A qué barra/cocina va este pedido?
                        </p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                          {barras.map(b => (
                            <button
                              key={b.id}
                              type="button"
                              onClick={() => setBarraSeleccionada(prev => prev === b.id ? null : b.id)}
                              className={`btn-metodo-pago ${barraSeleccionada === b.id ? 'seleccionado' : ''}`}
                            >
                              {b.nombre}
                            </button>
                          ))}
                        </div>
                      </>
                    ) : null}
                  </div>
                )}
              </div>
            )}

          </div>

          {mensajeError && (
            <div className="error-pago" style={{ padding: '12px', background: '#fff5f5', borderLeft: '5px solid #f05b41', margin: '20px 0', borderRadius: '8px', color: '#c53030', fontWeight: 'bold' }}>
              ⚠️ {mensajeError}
            </div>
          )}

          <div className="acciones-pago">
            <button
              onClick={handlePagar}
              className="btn-pagar"
              disabled={(() => {
                if (!metodoPago || calcularTotal() <= 0 || !puedeProcedarPago) return true;
                if (metodoPago === 'varios' && tipoVariosMode === 'montos') {
                  const sum = (parseFloat(montoEfectivo) || 0) + 
                              (parseFloat(montoTarjeta) || 0) + 
                              (parseFloat(montoYape) || 0) + 
                              (parseFloat(montoPlin) || 0);
                  return Math.abs(sum - calcularTotal()) > 0.01;
                }
                return false;
              })()}
            >
              Procesar Pago
            </button>
            <button
              onClick={generarComprobante}
              className="btn-comprobante"
              disabled={calcularTotal() <= 0 || imprimiendoComprobante}
            >
              {imprimiendoComprobante ? '⏳ Imprimiendo Comprobante...' : 'Imprimir Comprobante'}
            </button>
          </div>
        </div>
      </div>

      {mostrarConfirmacion && (
        <div className="modal-confirmacion">
          <div className="modal-content">
            <h3>Confirmar Pago</h3>
            
            {metodoPago === 'varios' && tipoVariosMode === 'montos' ? (
              <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                <p>¿Deseas procesar el pago mixto por un total de <strong>S/. {calcularTotal().toFixed(2)}</strong>?</p>
                <div style={{
                  background: '#f1f5f9',
                  padding: '12px 18px',
                  borderRadius: '8px',
                  margin: '12px auto',
                  textAlign: 'left',
                  fontSize: '0.9rem',
                  fontFamily: 'monospace',
                  display: 'inline-block',
                  minWidth: '220px'
                }}>
                  {parseFloat(montoEfectivo) > 0 && <div>💵 Efectivo: S/. {parseFloat(montoEfectivo).toFixed(2)}</div>}
                  {parseFloat(montoTarjeta) > 0 && <div>💳 Tarjeta:  S/. {parseFloat(montoTarjeta).toFixed(2)}</div>}
                  {parseFloat(montoYape) > 0 && <div>📱 Yape:     S/. {parseFloat(montoYape).toFixed(2)}</div>}
                  {parseFloat(montoPlin) > 0 && <div>🌀 Plin:     S/. {parseFloat(montoPlin).toFixed(2)}</div>}
                </div>
              </div>
            ) : (
              <p>¿Deseas procesar el pago por S/. {calcularTotal().toFixed(2)} con el método <strong>{metodoPago}</strong>?</p>
            )}

            {/* Selector/info de caja para MOZO y ENCARGADO */}
            {auth?.user?.rol !== 'CAJERO' && cajasAbiertas.length > 0 && (() => {
              // Si el pedido ya tiene caja asignada, mostrar info (no selector)
              if (pedido?.id_caja) {
                const cajaAsignada = cajasAbiertas.find(c => c.id === pedido.id_caja);
                const nombreCaja = cajaAsignada?.nombre || `Caja #${pedido.id_caja}`;
                return (
                  <div className="caja-asignada-info">
                    <span>📦 Pedido asignado a: <strong>{nombreCaja}</strong></span>
                  </div>
                );
              }
              // MOZO con caja asignada → mostrar info, no selector
              if (auth?.user?.rol === 'MOZO' && cajaAsignadaDB && idCajaCobro) {
                const cajaAsig = cajasAbiertas.find(c => c.id === idCajaCobro);
                return (
                  <div className="caja-asignada-info">
                    <span>🏧 Caja asignada: <strong>{cajaAsig?.nombre || `Caja #${idCajaCobro}`}</strong></span>
                  </div>
                );
              }
              // Caso: Solo hay una caja abierta en el sistema → mostrar info, no selector
              if (cajasAbiertas.length === 1) {
                const unicaCaja = cajasAbiertas[0];
                return (
                  <div className="caja-asignada-info">
                    <span>🏧 Caja de recepción: <strong>{unicaCaja?.nombre || `Caja #${unicaCaja?.id}`}</strong></span>
                  </div>
                );
              }
              // Sin caja asignada → mostrar selector
              return (
                <div className="selector-caja-cobro">
                  <label><strong>Caja de recepción:</strong></label>
                  <select
                    value={idCajaCobro || ''}
                    onChange={e => setIdCajaCobro(Number(e.target.value) || null)}
                  >
                    <option value="">-- Seleccionar caja --</option>
                    {cajasAbiertas.map(c => (
                      <option key={c.id} value={c.id}>{c.nombre || `Caja #${c.id}`}</option>
                    ))}
                  </select>
                </div>
              );
            })()}

            <div className="modal-buttons">
              <button onClick={() => setMostrarConfirmacion(false)} className="btn-cancelar-accion">
                Cancelar
              </button>
              <button
                onClick={confirmarPago}
                className="btn-confirmar"
                disabled={
                  auth?.user?.rol !== 'CAJERO' &&
                  cajasAbiertas.length > 0 &&
                  !pedido?.id_caja &&
                  !idCajaCobro
                }
              >
                Confirmar Pago
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comprobante con productos agrupados */}
      <div className="pago-print" id="comprobante">
        <div className="comprobante-header">
          <h2>COMPROBANTE DE PAGO</h2>
          <p><strong>Mesa:</strong> {pedido?.numero_mesa || `Mesa ${pedido?.id_mesa}`}</p>
          <p><strong>Fecha:</strong> {formatearFecha(new Date())}</p>
          <p><strong>Pedido N°:</strong> {pedidoId}</p>
        </div>

        <table className="comprobante-detalle">
          <thead>
            <tr>
              <th>Producto</th>
              <th>Cant.</th>
              <th>P.Unit</th>
              <th>Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {esPagoIndividual && detalle && detalle.estado === 'entregado' ? (
              <tr>
                <td>{detalle.nombre_producto || `Producto ${detalle.id_producto}`}</td>
                <td>{detalle.cantidad}</td>
                <td>S/. {detalle.precio_unitario}</td>
                <td>S/. {(detalle.cantidad * detalle.precio_unitario).toFixed(2)}</td>
              </tr>
            ) : (
              // Mostrar productos agrupados
              productosAgrupados.map((producto, index) => (
                <tr key={index}>
                  <td>{producto.nombre_producto || `Producto ${producto.id_producto}`}</td>
                  <td>{producto.cantidad_total || producto.cantidad}</td>
                  <td>S/. {producto.precio_unitario}</td>
                  <td>
                    S/. {((producto.cantidad_total || producto.cantidad) * producto.precio_unitario).toFixed(2)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan="3" className="total-label">Total:</td>
              <td className="total-value">S/. {calcularTotal().toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>

        <div className="comprobante-footer">
          <p><strong>Método de Pago:</strong> {metodoPago}</p>
          <p>¡Gracias por su preferencia!</p>
        </div>
      </div>
    </div>
  );
};

export default PagoComponent;